import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { portalTournamentAPI } from '../../../services/api';
import { isTabEditable } from '../../../utils/tournamentPhases';

/**
 * PortalRosterRequestTab - Shows current rules applied to the player's tee time
 * and allows overrides (when editable).
 */
const PortalRosterRequestTab = () => {
  const { tournamentId } = useParams();
  const { tournament, player, portalStatus } = useOutletContext();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [form, setForm] = useState({
    timePreference: 'no-preference',
    partnerRequests: [],
    specialRequests: '',
  });

  const editable = isTabEditable('request', portalStatus);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const res = await portalTournamentAPI.getRosterRequest(tournamentId);
        const data = res.data?.data || {};
        if (data.request) {
          setRequest(data.request);
        }
      } catch (err) {
        // 404 = no request yet, which is fine
        if (err.response?.status !== 404) {
          console.error('Failed to load roster request:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [tournamentId]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const res = await portalTournamentAPI.submitRosterRequest(tournamentId, form);
      setRequest(res.data?.data?.request || null);
      setShowOverrideForm(false);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to submit request:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getSourceLabel = (source) => {
    const labels = {
      'balboa-import': 'from Balboa signup',
      'member-defaults': 'from your profile',
      'portal-override': 'override applied',
      'td-manual': 'set by TD',
    };
    return labels[source] || source;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-6 w-6 border-4 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Introduction */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <p className="font-medium mb-1">How Your Tee Time Rules Work</p>
        <p className="text-xs text-blue-700">
          We show your original requests from Balboa, what we parsed from them, your profile defaults,
          and the final rules we'll use for pairing.
        </p>
      </div>

      {player ? (
        <>
          {/* Section 1: Original Balboa Request */}
          {(player.originalTimeRequest || player.originalPairingRequest) && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900">1. Your Original Balboa Signup</h3>
              </div>
              <div className="space-y-2">
                {player.originalTimeRequest && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Time Request</p>
                    <p className="text-sm text-gray-900">{player.originalTimeRequest}</p>
                  </div>
                )}
                {player.originalPairingRequest && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Pairing Request</p>
                    <p className="text-sm text-gray-900">{player.originalPairingRequest}</p>
                  </div>
                )}
                {!player.originalTimeRequest && !player.originalPairingRequest && (
                  <p className="text-sm text-gray-500 italic">No requests entered on Balboa</p>
                )}
              </div>
            </div>
          )}

          {/* Section 2: What We Parsed */}
          {(player.parsedTimePreference || player.parsedPartnerPreference ||
            player.selectedPairingPartners?.length > 0 || player.structuredTimeRequest?.type !== 'none') && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900">2. What We Parsed From That</h3>
              </div>
              <div className="space-y-2">
                {player.parsedTimePreference && (
                  <div className="flex items-start justify-between bg-blue-50 rounded-lg p-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Time Preference</p>
                      <p className="text-sm text-gray-900">{player.parsedTimePreference}</p>
                      {player.structuredTimeRequest?.originalText && (
                        <p className="text-xs text-gray-500 mt-1">→ {player.structuredTimeRequest.originalText}</p>
                      )}
                    </div>
                  </div>
                )}
                {(player.selectedPairingPartners?.length > 0 || player.parsedPartnerPreference) && (
                  <div className="flex items-start justify-between bg-blue-50 rounded-lg p-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Partner Preference</p>
                      <p className="text-sm text-gray-900">
                        {player.selectedPairingPartners?.join(', ') || player.parsedPartnerPreference}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 3: Profile Defaults (if source is member-defaults) */}
          {player.preferencesSource === 'member-defaults' && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <svg className="h-4 w-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <h3 className="font-semibold text-gray-900">3. Your Overall Profile Preferences</h3>
                <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                  Auto-Applied
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Since you didn't submit specific requests for this tournament, we used your saved profile preferences.
              </p>
              <div className="space-y-2">
                {player.parsedTimePreference && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Time Preference</p>
                    <p className="text-sm text-gray-900">{player.parsedTimePreference}</p>
                  </div>
                )}
                {player.selectedPairingPartners?.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 mb-1">Partner Preference</p>
                    <p className="text-sm text-gray-900">{player.selectedPairingPartners.join(', ')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 4: Final Rules Being Used */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 shadow-sm border-2 border-green-300">
            <div className="flex items-center gap-2 mb-3">
              <svg className="h-5 w-5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <h3 className="font-semibold text-green-900">Final Rules for Tee Sheet</h3>
              {player.preferencesSource && (
                <span className="ml-auto text-xs bg-white text-green-700 px-2 py-0.5 rounded-full border border-green-300">
                  {getSourceLabel(player.preferencesSource)}
                </span>
              )}
            </div>
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Time</p>
                <p className="text-sm font-medium text-gray-900">
                  {player.parsedTimePreference || player.structuredTimeRequest?.originalText || 'No preference'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Partners</p>
                <p className="text-sm font-medium text-gray-900">
                  {player.selectedPairingPartners?.length > 0
                    ? player.selectedPairingPartners.join(', ')
                    : player.parsedPartnerPreference || 'No preference'}
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">No player data available for this tournament.</p>
        </div>
      )}

      {/* Existing request status */}
      {request && (
        <div className={`rounded-xl p-4 border ${
          request.status === 'applied' ? 'bg-green-50 border-green-200' :
          request.status === 'denied' ? 'bg-red-50 border-red-200' :
          'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Override {request.status === 'applied' ? 'Applied' :
                       request.status === 'denied' ? 'Denied' :
                       request.status === 'acknowledged' ? 'Acknowledged' : 'Submitted'}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(request.submittedAt || request.createdAt).toLocaleDateString()}
            </span>
          </div>

          {request.timePreference && request.timePreference !== 'no-preference' && (
            <p className="text-sm text-gray-600">Time: {request.timePreference}</p>
          )}
          {request.partnerRequests?.length > 0 && (
            <p className="text-sm text-gray-600">
              Partners: {request.partnerRequests.map((p) => p.name).join(', ')}
            </p>
          )}
          {request.specialRequests && (
            <p className="text-sm text-gray-600">Special: {request.specialRequests}</p>
          )}
          {request.tdResponse && (
            <div className="mt-2 p-2 bg-white/50 rounded text-sm">
              <span className="font-medium">TD Response: </span>{request.tdResponse}
            </div>
          )}
        </div>
      )}

      {submitSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
          Override submitted successfully. Your tournament director will review it.
        </div>
      )}

      {/* Part 2: Override Form */}
      {editable && !showOverrideForm && !request && (
        <button
          onClick={() => setShowOverrideForm(true)}
          className="w-full py-3 bg-white border-2 border-dashed border-green-300 rounded-xl text-green-600 font-medium text-sm hover:bg-green-50 transition-colors"
        >
          Something different this week? Submit Override
        </button>
      )}

      {editable && showOverrideForm && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-3">Submit Override</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Preference</label>
              <select
                value={form.timePreference}
                onChange={(e) => setForm((prev) => ({ ...prev, timePreference: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="no-preference">No Preference</option>
                <option value="early-bird">Early Bird (First Available)</option>
                <option value="early">Early (7:00-8:00 AM)</option>
                <option value="mid-morning">Mid Morning (8:00-9:00 AM)</option>
                <option value="late-morning">Late Morning (9:00-10:00 AM)</option>
                <option value="midday">Midday (10:00-11:00 AM)</option>
                <option value="afternoon">Afternoon (After 11:00 AM)</option>
                <option value="last">Last (Final Tee Time)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
              <textarea
                value={form.specialRequests}
                onChange={(e) => setForm((prev) => ({ ...prev, specialRequests: e.target.value }))}
                placeholder="Any special requests for this week..."
                rows={3}
                maxLength={500}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{form.specialRequests.length}/500</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowOverrideForm(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!editable && !request && (
        <p className="text-xs text-gray-400 text-center">
          Override requests are only available during the roster-locked phase.
        </p>
      )}
    </div>
  );
};

export default PortalRosterRequestTab;
